import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { formatEuro } from "@/lib/catalog-format";
import { toast } from "sonner";
import { HardDrive, Calculator, ArrowRight } from "lucide-react";
import {
  ProjectMetaCard,
  useProjectMeta,
  escapeHtml,
  openPdfWindow,
  pdfHeader,
  pdfProjectCard,
  PDF_BASE_STYLES,
} from "@/lib/project-meta";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/hd-configurator")({
  head: () => {
    const url = `${SITE_URL}/hd-configurator`;
    const title = "HD Calculator — Bereken opslag voor je camerasysteem | Elvion Group Nederland";
    const description =
      "Bereken welke harde schijf je nodig hebt: kies aantal camera's, resolutie (MP) en bewaartermijn — vraag daarna eenvoudig een offerte aan.";

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: HdConfiguratorPage,
});

// Bitrate per camera per resolutie (Mbps, H.265 / Smart Codec, gemiddeld)
const BITRATE_MBPS: Record<string, number> = {
  "2": 2,
  "4": 4,
  "6": 6,
  "8": 8,
  "12": 12,
};

type HddOption = {
  slug: string;
  name: string;
  capacityTb: number;
  priceCents: number;
  inStock: boolean;
};

// Surveillance-grade HDD opties (WD Purple / Seagate SkyHawk)
const HDDS: HddOption[] = [
  { slug: "wd-purple-2tb", name: "WD Purple 2TB Surveillance HDD", capacityTb: 2, priceCents: 18900, inStock: false },
  { slug: "wd-purple-4tb", name: "WD Purple 4TB Surveillance HDD", capacityTb: 4, priceCents: 24900, inStock: false },
  { slug: "seagate-skyhawk-6tb", name: "Seagate SkyHawk 6TB Surveillance HDD", capacityTb: 6, priceCents: 23900, inStock: false },
  { slug: "wd-purple-8tb", name: "WD Purple 8TB Surveillance HDD", capacityTb: 8, priceCents: 49000, inStock: false },
  { slug: "seagate-skyhawk-10tb", name: "Seagate SkyHawk 10TB Surveillance HDD", capacityTb: 10, priceCents: 47600, inStock: false },
  { slug: "wd-purple-pro-12tb", name: "WD Purple Pro 12TB Surveillance HDD", capacityTb: 12, priceCents: 51000, inStock: false },
];

const FAQS = [
  {
    q: "Hoe wordt de benodigde opslag berekend?",
    a: "We rekenen met: aantal camera's × bitrate (Mbps) × 86.400 seconden per dag × bewaardagen / 8 / 1024 / 1024 = TB. De bitrate hangt af van de resolutie (MP) en gebruikt H.265 / Smart Codec waarden die door Hikvision en Dahua als praktijkrichtlijn worden gegeven.",
  },
  {
    q: "Wat is een realistische bitrate per camera?",
    a: "Voor H.265+ / Smart Codec rekenen wij standaard met 2 Mbps (2MP), 4 Mbps (4MP), 6 Mbps (6MP), 8 Mbps (8MP/4K) en 12 Mbps (12MP). Bij veel beweging in beeld kan dit oplopen, bij rustige scènes juist lager.",
  },
  {
    q: "Welke harde schijf moet ik kiezen voor mijn NVR?",
    a: "Gebruik altijd een surveillance-grade HDD zoals WD Purple of Seagate SkyHawk. Deze zijn ontworpen voor 24/7 schrijven, hebben firmware voor video-streams (ATA Streaming) en gaan veel langer mee dan een gewone desktop-schijf.",
  },
  {
    q: "Mag ik meerdere schijven combineren in één NVR?",
    a: "Ja. De meeste Hikvision en Dahua NVR's ondersteunen 2, 4 of 8 schijfslots. Je kunt de capaciteit dus uitbreiden of in RAID configureren voor extra zekerheid (afhankelijk van het model).",
  },
  {
    q: "Hoe lang mag ik beelden bewaren onder de AVG?",
    a: "Voor reguliere camerabewaking is de richtlijn maximaal 4 weken (28 dagen). Bij een geregistreerd incident mogen de relevante beelden langer worden bewaard tot het incident is afgehandeld.",
  },
  {
    q: "Telt geluidsopname mee in de opslagberekening?",
    a: "Audio voegt circa 64–128 kbps per kanaal toe. Dat is verwaarloosbaar (<2%) ten opzichte van de videostream en zit binnen de marges van onze berekening.",
  },
  {
    q: "Wat als ik later meer camera's wil toevoegen?",
    a: "Reken bij voorkeur 20–30% extra capaciteit. Onze calculator toont automatisch de eerstvolgende grotere HDD die past, zodat je ruimte hebt voor uitbreiding of pieken in beweging.",
  },
  {
    q: "Kunnen jullie de schijf ook direct in de NVR plaatsen?",
    a: "Ja, bij bestelling van een complete set installeren wij de HDD kosteloos in de NVR en formatteren we deze voor direct gebruik. Vermeld dit even bij je bestelling of vraag het je adviseur.",
  },
];

type HdState = {
  cameras: number;
  mp: string;
  days: number;
  bufferPct: number;
};

function buildHdPdfHtml(input: {
  projectNumber: string;
  customerName: string;
  projectDate: string;
  cameras: number;
  mp: string;
  days: number;
  bufferPct: number;
  bitrate: number;
  dailyGb: number;
  neededTb: number;
  neededGb: number;
  recommended: HddOption;
}): string {
  return `<!doctype html><html lang="nl"><head><meta charset="utf-8" />
<title>Bestellijst opslag ${escapeHtml(input.projectNumber || "")} — Elvion Group Nederland</title>
<style>${PDF_BASE_STYLES}</style></head><body>
${pdfHeader("Bestellijst opslag")}
<h1>Bestellijst opslag (camerasysteem)</h1>
<div class="grid">
  ${pdfProjectCard({ projectNumber: input.projectNumber, customerName: input.customerName, projectDate: input.projectDate })}
  <div class="card">
    <h3>Configuratie</h3>
    <div class="row"><span class="k">Camera's</span><span class="v">${input.cameras}× @ ${escapeHtml(input.mp)}MP</span></div>
    <div class="row"><span class="k">Bitrate</span><span class="v">${input.bitrate} Mbps (H.265/Smart)</span></div>
    <div class="row"><span class="k">Bewaartermijn</span><span class="v">${input.days} dagen</span></div>
    <div class="row"><span class="k">Reservecapaciteit</span><span class="v">+${input.bufferPct}%</span></div>
    <div class="row"><span class="k">Benodigde opslag</span><span class="v">${input.neededTb >= 1 ? `${input.neededTb.toFixed(2)} TB` : `${Math.round(input.neededGb)} GB`}</span></div>
    <div class="row"><span class="k">Per dag</span><span class="v">≈ ${Math.round(input.dailyGb)} GB</span></div>
  </div>
</div>
<h2>Aanbevolen harde schijf</h2>
<table>
  <thead><tr><th>Model</th><th class="num">Capaciteit</th><th class="num">Aantal</th><th class="num">Prijs (excl. btw)</th></tr></thead>
  <tbody>
    <tr><td><strong>${escapeHtml(input.recommended.name)}</strong></td><td class="num">${input.recommended.capacityTb} TB</td><td class="num">1×</td><td class="num">€ ${(input.recommended.priceCents / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
  </tbody>
</table>
<div class="total">
  <div class="lbl">Indicatieve materiaalkosten</div>
  <div class="val">€ ${(input.recommended.priceCents / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
  <div style="font-size:10.5px; color:#5a6b7b; margin-top:4px;">Excl. installatie, NVR en btw. Surveillance-grade schijf (WD Purple of Seagate SkyHawk) aanbevolen voor 24/7 opname.</div>
</div>
<p class="foot">Gegenereerd door de Elvion Group Nederland HD Calculator.</p>
</body></html>`;
}

function HdConfiguratorPage() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState(4);
  const [mp, setMp] = useState<string>("4");
  const [days, setDays] = useState(14);
  const [bufferPct, setBufferPct] = useState(20);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const meta = useProjectMeta<HdState>({
    storageKey: "hd-calculator:project:v1",
    autosaveTable: "hd_autosave",
    configurationsTable: "hd_configurations",
    getState: () => ({ cameras, mp, days, bufferPct }),
    applyState: (s) => {
      if (typeof s.cameras === "number") setCameras(s.cameras);
      if (typeof s.mp === "string") setMp(s.mp);
      if (typeof s.days === "number") setDays(s.days);
      if (typeof s.bufferPct === "number") setBufferPct(s.bufferPct);
    },
  });

  // push debounced autosave whenever state changes
  useEffect(() => {
    meta.pushAutosave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cameras,
    mp,
    days,
    bufferPct,
    meta.projectNumber,
    meta.customerName,
    meta.projectDate,
    meta.userId,
  ]);

  const { neededTb, neededGb, recommended, dailyGb } = useMemo(() => {
    const bitrate = BITRATE_MBPS[mp] ?? 4;
    const gbPerCamPerDay = (bitrate * 86400) / 8 / 1024;
    const dailyGb = gbPerCamPerDay * cameras;
    const totalGb = dailyGb * days * (1 + bufferPct / 100);
    const totalTb = totalGb / 1024;
    const recommended =
      HDDS.find((h) => h.capacityTb >= totalTb) ?? HDDS[HDDS.length - 1];
    return { neededGb: totalGb, neededTb: totalTb, recommended, dailyGb };
  }, [cameras, mp, days, bufferPct]);

  const requestQuote = () => {
    navigate({ to: "/offerte-aanvragen", search: { product: "camerabewaking" } });
  };

  const printOrderPdf = () => {
    const html = buildHdPdfHtml({
      projectNumber: meta.projectNumber,
      customerName: meta.customerName,
      projectDate: meta.projectDate,
      cameras,
      mp,
      days,
      bufferPct,
      bitrate: BITRATE_MBPS[mp] ?? 4,
      dailyGb,
      neededTb,
      neededGb,
      recommended,
    });
    openPdfWindow(html);
  };

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          HD Calculator: bereken de juiste opslag voor je camerasysteem
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Hoeveel opslag heb je nodig voor je beveiligingscamera's? Vul het aantal
          camera's, de resolutie en de gewenste bewaartermijn in. Wij berekenen
          direct welke <strong>surveillance-grade harde schijf</strong> (WD Purple
          of Seagate SkyHawk) past en je vraagt daarna eenvoudig een offerte aan.
        </p>

        <ProjectMetaCard {...meta} onPrintPdf={printOrderPdf} />

        {/* Configurator */}
        <section className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Calculator
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Aantal camera's
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {cameras}
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={32}
                  value={cameras}
                  onChange={(e) => setCameras(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>1</span>
                  <span>32</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">
                  Resolutie per camera
                </label>
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {Object.keys(BITRATE_MBPS).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setMp(k)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                        mp === k
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      {k}MP
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  Bitrate: {BITRATE_MBPS[mp]} Mbps per camera (H.265 / Smart Codec)
                </p>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Bewaartermijn (dagen)
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {days}
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={60}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>1 dag</span>
                  <span>AVG-max ±28</span>
                  <span>60 dagen</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Reservecapaciteit
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    +{bufferPct}%
                  </span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={50}
                  step={5}
                  value={bufferPct}
                  onChange={(e) => setBufferPct(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <p className="mt-1 text-xs text-text-secondary">
                  Aanbevolen: 20% buffer voor uitbreiding en bewegingspieken.
                </p>
              </div>
            </div>
          </div>

          {/* Resultaat */}
          <div className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <HardDrive className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Aanbevolen schijf
              </span>
            </div>

            <p className="text-sm text-text-secondary">Benodigde opslag</p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground">
              {neededTb >= 1
                ? `${neededTb.toFixed(2)} TB`
                : `${Math.round(neededGb)} GB`}
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              ≈ {Math.round(dailyGb)} GB per dag voor {cameras} camera('s) @ {mp}MP
            </p>

            <div className="mt-6 rounded-xl border border-border bg-background p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Onze keuze
              </p>
              <p className="mt-1 text-lg font-bold text-foreground">
                {recommended.name}
              </p>
              <p className="mt-1 text-2xl font-bold text-orange">
                {formatEuro(recommended.priceCents)}
                <span className="ml-1 text-xs font-medium text-text-secondary">
                  excl. btw
                </span>
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={requestQuote}
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange/90"
                >
                  Offerte aanvragen
                </button>
                <Link
                  to="/c/$category"
                  params={{ category: "beveiliging" }}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
                >
                  Naar product
                  <ArrowRight className="size-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Alle HDD opties */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            Alle surveillance harde schijven
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Liever zelf kiezen? Hieronder alle opties — geschikt voor 24/7 opname.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3">Capaciteit</th>
                  <th className="px-4 py-3">Prijs</th>
                  <th className="px-4 py-3 text-right">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {HDDS.map((h) => {
                  const fits = h.capacityTb >= neededTb;
                  return (
                    <tr key={h.slug} className={fits ? "" : "opacity-60"}>
                      <td className="px-4 py-3 font-semibold text-foreground">
                        {h.name}
                        {!h.inStock && (
                          <span className="ml-2 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary">
                            Niet op voorraad
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{h.capacityTb} TB</td>
                      <td className="px-4 py-3">
                        {formatEuro(h.priceCents)}{" "}
                        <span className="text-xs text-text-secondary">
                          excl. btw
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={requestQuote}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:border-orange hover:text-orange"
                        >
                          Offerte
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Voordelen */}
        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Waarom surveillance-grade?
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Ontworpen voor 24/7 schrijven (24×7 workload)",
                "ATA Streaming firmware voor stabiele videostreams",
                "Tot 64 HD-camerakanalen ondersteund",
                "Lage warmteproductie en geluid",
                "Tot 3 jaar fabrieksgarantie",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Tips voor je NVR</h2>
            <ul className="mt-3 space-y-2">
              {[
                "Gebruik bij voorkeur identieke schijven per NVR",
                "Reken altijd 20% extra capaciteit voor uitbreiding",
                "Schakel Smart Codec / H.265+ in om opslag te halveren",
                "Plan event-opname i.p.v. continue opname om opslag te besparen",
                "Backup belangrijke beelden direct naar een NAS of cloud",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen over opslag
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${isOpen ? "rotate-180" : ""}`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && (
                    <p className="mt-2 text-text-secondary">{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Twijfel je over de juiste configuratie?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze adviseurs helpen je graag bij het kiezen van de juiste NVR,
            camera's en opslag. Compleet pakket samenstellen of installatie
            inclusief? Wij regelen het.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/c/$category"
              params={{ category: "beveiliging" }}
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Bekijk alle beveiligingsproducten
            </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
